import { CommonModule } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	effect,
	inject,
	input,
	OnInit,
	output,
	signal,
	untracked,
	ChangeDetectionStrategy,
} from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { LayoutConfig } from '../../../models/layout.model';
import { requirePageLayout, type PageConfig } from '../../../models/page.model';
import { ThemeConfig } from '../../../models/theme.model';
import { LayoutService } from '../../../services/layout.service';
import { JeptoLayoutContainerComponent } from '../jepto-layout-container/jepto-layout-container.component';
import { deriveLayoutIdForPage } from '../../../utils/page-layout-id.util';
import type { DateRangeExpression } from '../../../models/date-range-expression.model';

/**
 * Page component for displaying a single dashboard page
 *
 * The Page component renders a collection of layouts and handles date range selection.
 * It serves as an intermediary between the View component and Layout components.
 *
 * Features:
 * - Date range selection with calendar picker
 * - Layout rendering
 * - Date range change propagation (Page → View → App)
 * - View-only mode pass-through to layouts
 *
 * @example
 * ```html
 * <jepto-page-container
 *   [pageConfig]="pageConfiguration"
 *   [theme]="'light'"
 *   [viewOnly]="true"
 *   (dateRangeChange)="onDateRangeChange($event)">
 * </jepto-page-container>
 * ```
 */
@Component({
	selector: 'jepto-page-container',
	templateUrl: './jepto-page-container.component.html',
	styleUrls: ['./jepto-page-container.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		MatFormFieldModule,
		MatSelectModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		JeptoLayoutContainerComponent,
	],
})
export class JeptoPageComponent implements OnInit {
	/** The page configuration defining layouts and cards */
	readonly pageConfig = input<PageConfig>();

	/** Page ID to load (alternative to providing pageConfig) */
	readonly pageId = input<string>();

	/** View ID this page belongs to */
	readonly viewId = input<string>();

	/** Account ID for debug overlays (passed through to layout) */
	readonly accountId = input<string>();

	/** Theme mode (light/dark/auto) — passed to layout as themeMode for chart colours */
	readonly theme = input<'light' | 'dark' | 'auto'>();

	/** Color palette identifier */
	readonly palette = input<string>('pal_jepto');

	/** Theme configuration object */
	readonly themeConfig = input<ThemeConfig>();

	/** Layout options for the grid */
	readonly layoutOptions = input<any>({});

	/** Layouts to be displayed in the page */
	private layoutsSignal = signal<LayoutConfig[]>([]);

	/** Loading state */
	loading = signal<boolean>(false);

	/** Date range */
	dateRangeSignal = signal<{ startDate: string; endDate: string }>({
		startDate: '',
		endDate: '',
	});

	/** Error message */
	error = signal<string | null>(null);

	/**
	 * View-only mode - when true, passed to layouts to make them static
	 * Prevents dragging and resizing of cards
	 */
	readonly viewOnly = input<boolean>(false);

	/**
	 * Report-level default date range for date-range filter cards (view settings / getView).
	 */
	readonly reportDateRangeExpression = input<
		DateRangeExpression | undefined
	>();

	/**
	 * Output event emitted when date range changes
	 * Contains { startDate, endDate } in YYYY-MM-DD format
	 */
	readonly dateRangeChange = output<{
		startDate: string;
		endDate: string;
	}>();

	/** Emitted when a date-range filter card applies a new expression. */
	readonly filterDateRangeChange = output<DateRangeExpression>();

	private layoutService = inject(LayoutService);
	private cdr = inject(ChangeDetectorRef);

	constructor() {
		effect(() => {
			const config = this.pageConfig();
			if (config) {
				// Use untracked to prevent effect from tracking signals read during initialization
				// (e.g., layoutState signals), which would cause infinite loops
				untracked(() => this.initializePage(config));
			} else if (this.pageId() && !this.pageConfig()) {
				untracked(() => this.loadPageById());
			}
		});
	}

	ngOnInit(): void {
		const config = this.pageConfig();
		if (config) {
			this.initializePage(config);
		} else if (this.pageId() && !config) {
			this.loadPageById();
		}
	}

	private initializePage(pageConfig: PageConfig): void {
		if (!pageConfig) return;

		this.loadLayoutsForPage(pageConfig);

		// Mark for check instead of using setTimeout - Angular will handle the update
		this.cdr.markForCheck();
	}

	/**
	 * Get the current layouts
	 */
	get layouts(): LayoutConfig[] {
		return this.layoutsSignal();
	}

	/**
	 * Load the page data based on pageId (when pageConfig is not provided)
	 * Note: This is only used when pageId is provided without pageConfig
	 */
	private loadPageById(): void {
		if (this.pageId()) {
			console.log(
				'[PageComponent] Error: Page loading by ID not implemented'
			);
			this.loading.set(false);
			this.error.set('Page loading by ID not implemented');
			return;
		}

		console.log('[PageComponent] Error: No page configuration provided');
		this.loading.set(false);
		this.error.set('No page configuration provided.');
	}

	/**
	 * Load layouts for a given page
	 */
	private loadLayoutsForPage(page: PageConfig): void {
		const cards = requirePageLayout(page);
		if (cards.length === 0) {
			console.log('[PageComponent] Page has no layouts');
			this.layoutsSignal.set([]);
			this.loading.set(false);
			return;
		}

		try {
			const layout: LayoutConfig = {
				layoutId: deriveLayoutIdForPage(page.pageId),
				pageId: page.pageId,
				order: page.order ?? 1,
				cards,
			};

			this.layoutService.setLayoutConfig(layout);
			this.layoutsSignal.set([layout]);
			this.loading.set(false);
		} catch (err) {
			console.error(
				`[PageComponent] Error loading layouts: ${
					err.message || 'Unknown error'
				}`
			);
			this.error.set(
				'Error loading layouts: ' + (err.message || 'Unknown error')
			);
			this.loading.set(false);
		}
	}

	/**
	 * Handle date range selection from the select dropdown
	 */
	onSelectDateCoverage(event: any): void {
		const formatDateToYYYYMMDD = (date: Date): string => {
			return date.toISOString().split('T')[0];
		};

		// Format dates to YYYY-MM-DD
		let startDate = event.startDate;
		let endDate = event.endDate;

		// Convert to YYYY-MM-DD if needed
		if (event.startDate) {
			startDate =
				typeof event.startDate !== 'string' ||
				!event.startDate.match(/^\d{4}-\d{2}-\d{2}$/)
					? formatDateToYYYYMMDD(new Date(event.startDate))
					: event.startDate;
		}

		if (event.endDate) {
			endDate =
				typeof event.endDate !== 'string' ||
				!event.endDate.match(/^\d{4}-\d{2}-\d{2}$/)
					? formatDateToYYYYMMDD(new Date(event.endDate))
					: event.endDate;
		}

		const dateRange = { startDate, endDate };
		this.dateRangeSignal.set(dateRange);
		this.dateRangeChange.emit(dateRange);
	}
}
